import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { UserPicker } from '../../components/UserPicker';
import { Textarea } from '../../components/ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, Search, MoreVertical, Phone, MessageCircle, ExternalLink, Star, MapPin, Eye, Upload, Download, FileSpreadsheet, CheckCircle2, AlertTriangle, X, Edit, ArrowUp, ArrowDown, Filter, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirmation } from '../../components/ConfirmationProvider';
import { SmbOutreachAutomationBanner } from '../../components/smb/SmbOutreachAutomationBanner';

const STATUS_COLORS = {
  new: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  preview_ready: 'bg-violet-500/15 text-violet-400 border-violet-500/30',
  preview_qa_failed: 'bg-red-500/15 text-red-400 border-red-500/30',
  preview_qa_passed: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  contacted: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
  call_declined: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
  preview_sent: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  interested: 'bg-green-500/15 text-green-400 border-green-500/30',
  follow_up: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
  interest_lost: 'bg-rose-500/15 text-rose-400 border-rose-500/30',
  commercials_shared: 'bg-sky-500/15 text-sky-400 border-sky-500/30',
  payment_received: 'bg-green-600/15 text-green-500 border-green-600/30',
  tweaks_in_progress: 'bg-fuchsia-500/15 text-fuchsia-400 border-fuchsia-500/30',
  live_ready: 'bg-violet-600/15 text-violet-400 border-violet-600/30',
  live_deployed: 'bg-teal-500/15 text-teal-400 border-teal-500/30',
  live_qa_failed: 'bg-red-600/15 text-red-500 border-red-600/30',
  live_qa_passed: 'bg-emerald-600/15 text-emerald-500 border-emerald-600/30',
  live_sent: 'bg-blue-600/15 text-blue-400 border-blue-600/30',
  lost: 'bg-gray-500/15 text-gray-400 border-gray-500/30',
};

const emptyForm = {
  business_name: '', category: '', contact_name: '', phone: '', whatsapp: '',
  email: '', area: '', city: '', google_maps_url: '', google_rating: '',
  notes: '', preview_url: '', live_url: '', status: 'new', assigned_to: '',
};

export const SMBLeadList = () => {
  const navigate = useNavigate();
  const confirm = useConfirmation();
  // ───────────────────────────────────────────────────────────────
  // URL is the source of truth for filters/sort/page so refreshes,
  // bookmarks, and back/forward navigation all preserve the view.
  // ───────────────────────────────────────────────────────────────
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';
  const initialStatus = searchParams.get('status') || 'all';
  const initialCategory = searchParams.get('category') || 'all';
  const initialCity = searchParams.get('city') || '';
  const initialFollowup = searchParams.get('followup') || 'all';
  const initialFrom = searchParams.get('from') || '';
  const initialTo = searchParams.get('to') || '';
  const initialSort = searchParams.get('sort') === 'asc' ? 'asc' : 'desc';
  const initialPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);

  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [statuses, setStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [cities, setCities] = useState([]);
  const [search, setSearch] = useState(initialSearch);
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [categoryFilter, setCategoryFilter] = useState(initialCategory);
  const [categoryOptions, setCategoryOptions] = useState([]);
  // Active categories (slug+label) that power the Add/Edit form Select.
  // Loaded from /api/smb/categories — admin-managed via Settings.
  const [formCategories, setFormCategories] = useState([]);
  const [cityFilter, setCityFilter] = useState(initialCity);
  const [followupFilter, setFollowupFilter] = useState(initialFollowup);
  const [dateFrom, setDateFrom] = useState(initialFrom);
  const [dateTo, setDateTo] = useState(initialTo);
  const [sortDir, setSortDir] = useState(initialSort);
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(100);
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);

  // Status change modal state
  const [statusModal, setStatusModal] = useState(false);
  const [statusLead, setStatusLead] = useState(null);
  const [statusValue, setStatusValue] = useState('');
  const [statusRemark, setStatusRemark] = useState('');
  const [statusPreviewUrl, setStatusPreviewUrl] = useState('');
  const [statusLiveUrl, setStatusLiveUrl] = useState('');

  const PREVIEW_URL_STATUSES = ['preview_ready', 'preview_qa_passed', 'preview_qa_failed', 'preview_sent', 'tweaks_in_progress', 'live_ready', 'live_qa_passed', 'live_qa_failed', 'live_sent', 'live_deployed'];
  const LIVE_URL_STATUSES = ['live_ready', 'live_qa_passed', 'live_qa_failed', 'live_sent', 'live_deployed'];

  const fetchLeads = useCallback(async () => {
    try {
      let url = `/smb/leads?limit=${pageSize}&page=${page}&sort=${sortDir}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (statusFilter !== 'all') url += `&status=${statusFilter}`;
      if (categoryFilter !== 'all') url += `&category=${encodeURIComponent(categoryFilter)}`;
      if (cityFilter) url += `&city=${encodeURIComponent(cityFilter)}`;
      if (followupFilter !== 'all') url += `&followup_filter=${followupFilter}`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      const res = await api.get(url);
      if (res.data.success) { setLeads(res.data.data); setTotal(res.data.total); }
    } catch { toast.error('Failed to load leads'); }
  }, [search, statusFilter, categoryFilter, cityFilter, followupFilter, dateFrom, dateTo, sortDir, page, pageSize]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  // Mirror every filter/sort/page into the URL — the next refresh, deep-link
  // share, or browser-back will restore the exact same view.
  useEffect(() => {
    const next = {};
    if (search) next.search = search;
    if (statusFilter !== 'all') next.status = statusFilter;
    if (categoryFilter !== 'all') next.category = categoryFilter;
    if (cityFilter) next.city = cityFilter;
    if (followupFilter !== 'all') next.followup = followupFilter;
    if (dateFrom) next.from = dateFrom;
    if (dateTo) next.to = dateTo;
    if (sortDir !== 'desc') next.sort = sortDir;
    if (page !== 1) next.page = String(page);
    setSearchParams(next, { replace: true });
  }, [search, statusFilter, categoryFilter, cityFilter, followupFilter, dateFrom, dateTo, sortDir, page, setSearchParams]);

  // Active-filter count — drives the header pill so users always see at a
  // glance whether they're looking at a filtered slice. Sort doesn't count.
  const activeFilters = useMemo(() => {
    let n = 0;
    if (search) n++;
    if (statusFilter !== 'all') n++;
    if (categoryFilter !== 'all') n++;
    if (cityFilter) n++;
    if (followupFilter !== 'all') n++;
    if (dateFrom) n++;
    if (dateTo) n++;
    return n;
  }, [search, statusFilter, categoryFilter, cityFilter, followupFilter, dateFrom, dateTo]);

  const clearAllFilters = () => {
    setSearch(''); setStatusFilter('all'); setCategoryFilter('all');
    setCityFilter(''); setFollowupFilter('all'); setDateFrom(''); setDateTo('');
    setPage(1);
  };

  const toggleSort = () => { setSortDir((d) => (d === 'desc' ? 'asc' : 'desc')); setPage(1); };

  // Any filter / search change should send us back to page 1 — otherwise
  // a 2,000-lead dataset filtered to 12 results on page 23 would render empty.
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, categoryFilter, cityFilter, followupFilter, dateFrom, dateTo, pageSize]);

  useEffect(() => {
    const load = async () => {
      try {
        const [sRes, uRes, cRes, catRes, allCatRes] = await Promise.all([
          api.get('/smb/statuses'),
          api.get('/smb/users'),
          api.get('/smb/leads/distinct-cities'),
          api.get('/smb/leads/distinct-categories'),
          // Admin-managed category list (active only) — powers the Add/Edit form.
          api.get('/smb/categories?active_only=true'),
        ]);
        if (sRes.data.success) setStatuses(sRes.data.data);
        if (uRes.data.success) setUsers(uRes.data.data);
        if (cRes.data.success) setCities(cRes.data.data || []);
        const adminSlugs = (allCatRes.data?.data || []).map(c => c.slug);
        if (allCatRes.data?.success) setFormCategories(allCatRes.data.data || []);
        if (catRes.data.success) {
          // Merge admin-active categories with categories already tagged on
          // leads so filter shows everything (even legacy/deactivated tags).
          const merged = Array.from(new Set([
            ...(catRes.data.data || []),
            ...adminSlugs,
          ])).filter(Boolean).sort((a, b) => a.localeCompare(b));
          setCategoryOptions(merged);
        }
      } catch {}
    };
    load();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Re-entry guard. Without this, a double-click on "Add Lead" — or a
    // single click that lands during a backend restart / slow response —
    // fires multiple POSTs in parallel, all of which complete on the
    // freshly-restarted instance and insert duplicate rows with the same
    // business_name + phone (different UUIDs, near-identical timestamps).
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const payload = { ...form, google_rating: form.google_rating ? parseFloat(form.google_rating) : null };
      if (editId) {
        await api.put(`/smb/leads/${editId}`, payload);
        toast.success('Lead updated');
      } else {
        await api.post('/smb/leads', payload);
        toast.success('Lead added');
      }
      setIsOpen(false); setForm(emptyForm); setEditId(null); fetchLeads();
    } catch { toast.error('Operation failed'); }
    finally { setIsSubmitting(false); }
  };

  const handleEdit = (lead) => {
    setForm({
      business_name: lead.business_name, category: lead.category, contact_name: lead.contact_name,
      phone: lead.phone, whatsapp: lead.whatsapp, email: lead.email, area: lead.area, city: lead.city,
      google_maps_url: lead.google_maps_url, google_rating: lead.google_rating || '',
      notes: lead.notes, preview_url: lead.preview_url, live_url: lead.live_url,
      status: lead.status, assigned_to: lead.assigned_to || '',
    });
    setEditId(lead.id);
    setIsOpen(true);
  };

  const openStatusModal = (lead) => {
    setStatusLead(lead);
    setStatusValue(lead.status);
    setStatusRemark('');
    setStatusPreviewUrl(lead.preview_url || '');
    setStatusLiveUrl(lead.live_url || '');
    setStatusModal(true);
  };

  const isValidUrl = (str) => {
    try { const u = new URL(str); return u.protocol === 'http:' || u.protocol === 'https:'; } catch { return false; }
  };

  const handleStatusChange = async () => {
    if (!statusLead) return;
    if (PREVIEW_URL_STATUSES.includes(statusValue) && !statusPreviewUrl.trim()) {
      toast.error('Preview URL is required for this status'); return;
    }
    if (PREVIEW_URL_STATUSES.includes(statusValue) && statusPreviewUrl.trim() && !isValidUrl(statusPreviewUrl.trim())) {
      toast.error('Preview URL must be a valid URL (e.g. https://...)'); return;
    }
    if (LIVE_URL_STATUSES.includes(statusValue) && !statusLiveUrl.trim()) {
      toast.error('Live URL is required for this status'); return;
    }
    if (LIVE_URL_STATUSES.includes(statusValue) && statusLiveUrl.trim() && !isValidUrl(statusLiveUrl.trim())) {
      toast.error('Live URL must be a valid URL (e.g. https://...)'); return;
    }
    try {
      await api.put(`/smb/leads/${statusLead.id}/status`, {
        status: statusValue,
        remark: statusRemark,
        preview_url: statusPreviewUrl,
        live_url: statusLiveUrl,
      });
      toast.success(`Status updated to ${statuses.find(s => s.value === statusValue)?.label || statusValue}`);
      setStatusModal(false);
      fetchLeads();
    } catch { toast.error('Failed to update status'); }
  };

  const handleDelete = async (lead) => {
    const confirmed = await confirm({ title: 'Delete Lead', description: `Are you sure you want to delete "${lead.business_name}"? This action cannot be undone.`, confirmText: 'Delete' });
    if (!confirmed) return;
    try { await api.delete(`/smb/leads/${lead.id}`); toast.success('Lead deactivated'); fetchLeads(); } catch { toast.error('Failed'); }
  };

  const getStatusBadge = (status) => {
    const s = statuses.find(st => st.value === status);
    return <Badge className={`${STATUS_COLORS[status] || ''} border`} data-testid={`status-badge-${status}`}>{s?.label || status}</Badge>;
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get('/smb/leads/import-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'smb_leads_template.csv'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download template'); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) { toast.error('Please select a CSV file'); return; }
    setImportFile(file);
    setImportResult(null);
    // Parse preview
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) { toast.error('CSV file appears empty'); return; }
      const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
      const rows = lines.slice(1).map((line, i) => {
        const values = line.match(/(".*?"|[^,]+)/g)?.map(v => v.trim().replace(/^"|"$/g, '')) || [];
        const row = {};
        headers.forEach((h, j) => { row[h] = values[j] || ''; });
        row._row = i + 2;
        return row;
      });
      setImportPreview({ headers, rows: rows.slice(0, 50), totalRows: rows.length });
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await api.post('/smb/leads/bulk-import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.success) {
        setImportResult(res.data.data);
        toast.success(`Imported ${res.data.data.imported} leads`);
        fetchLeads();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };

  const resetImport = () => { setImportFile(null); setImportPreview(null); setImportResult(null); };

  return (
    <div className="space-y-5 animate-fade-in" data-testid="smb-lead-list">
      <SmbOutreachAutomationBanner />
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            SMB Leads
            {activeFilters > 0 && (
              <Badge
                className="bg-amber-500/15 text-amber-500 border border-amber-500/30 text-xs inline-flex items-center gap-1 cursor-pointer hover:bg-amber-500/25"
                onClick={clearAllFilters}
                title="Click to clear all filters"
                data-testid="active-filters-badge"
              >
                <Filter className="h-3 w-3" />
                {activeFilters} filter{activeFilters > 1 ? 's' : ''} active
                <X className="h-3 w-3 ml-0.5" />
              </Badge>
            )}
          </h1>
          <p className="text-muted-foreground text-sm">{total} businesses tracked</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { resetImport(); setImportOpen(true); }} data-testid="import-csv-btn">
            <Upload className="h-4 w-4 mr-2" />Import CSV
          </Button>
          <Button onClick={() => { setForm(emptyForm); setEditId(null); setIsOpen(true); }} data-testid="add-smb-lead-btn">
            <Plus className="h-4 w-4 mr-2" />Add Lead
          </Button>
        </div>
      </div>

      <Card><CardContent className="py-3">
        <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search business, contact, phone, project ID..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="smb-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]" data-testid="smb-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {statuses.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[160px]" data-testid="smb-category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categoryOptions.map(c => (
                <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={cityFilter || 'all'} onValueChange={v => setCityFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[170px]" data-testid="smb-city-filter"><SelectValue placeholder="City" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Cities</SelectItem>
              {cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={followupFilter} onValueChange={setFollowupFilter}>
            <SelectTrigger className="w-[170px]" data-testid="smb-followup-filter"><SelectValue placeholder="Follow-ups" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Leads</SelectItem>
              <SelectItem value="today">Follow-up Today</SelectItem>
              <SelectItem value="this_week">Follow-up This Week</SelectItem>
              <SelectItem value="overdue">Overdue Follow-ups</SelectItem>
              <SelectItem value="upcoming">Upcoming Follow-ups</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1">
            <Input
              type="date" value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="w-[150px]"
              title="Added from"
              data-testid="smb-date-from"
            />
            <span className="text-muted-foreground text-xs">→</span>
            <Input
              type="date" value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="w-[150px]"
              title="Added to"
              data-testid="smb-date-to"
            />
            {(dateFrom || dateTo) && (
              <Button
                variant="ghost" size="sm"
                className="text-xs px-2"
                onClick={() => { setDateFrom(''); setDateTo(''); }}
                data-testid="smb-date-clear"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </CardContent></Card>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Business</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Area / City</TableHead>
              <TableHead>Rating</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Latest Remark</TableHead>
              <TableHead>Assigned To</TableHead>
              <TableHead>
                <button
                  type="button"
                  onClick={toggleSort}
                  className="inline-flex items-center gap-1 hover:text-foreground transition-colors font-medium"
                  title={`Sort by date added (${sortDir === 'desc' ? 'newest first' : 'oldest first'})`}
                  data-testid="sort-added-toggle"
                  data-sort-dir={sortDir}
                >
                  Added
                  {sortDir === 'desc'
                    ? <ArrowDown className="h-3 w-3 text-primary" />
                    : <ArrowUp className="h-3 w-3 text-primary" />}
                </button>
              </TableHead>
              <TableHead className="w-[50px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {leads.map(lead => (
              <TableRow key={lead.id} className="cursor-pointer hover:bg-muted/30" data-testid={`smb-lead-row-${lead.id}`}>
                <TableCell>
                  <Link to={`/smb/leads/${lead.id}`} className="text-left hover:text-primary transition-colors inline-block" data-testid={`smb-lead-name-link-${lead.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium">{lead.business_name}</p>
                      {lead.category && (
                        <Badge
                          variant="secondary"
                          className="text-[10px] font-normal capitalize px-1.5 py-0 h-4"
                          data-testid={`smb-category-badge-${lead.id}`}
                        >
                          {lead.category}
                        </Badge>
                      )}
                      {lead.latest_wa_delivery_failed && (
                        <Badge
                          className="text-[10px] font-normal px-1.5 py-0 h-4 bg-red-500/15 text-red-400 border-red-500/30"
                          title="Most recent WhatsApp outreach failed to deliver — click to open the lead and copy the activation link manually"
                          data-testid={`wa-failed-badge-${lead.id}`}
                        >
                          <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />WA delivery failed
                        </Badge>
                      )}
                    </div>
                  </Link>
                  {(lead.preview_url || lead.live_url) && (
                    <div className="flex items-center gap-3 mt-1">
                      {lead.preview_url && (
                        <a href={lead.preview_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-[11px] text-muted-foreground hover:text-primary hover:underline inline-flex items-center gap-1" data-testid={`preview-link-${lead.id}`}>
                          <ExternalLink className="h-3 w-3" />Preview
                        </a>
                      )}
                      {lead.live_url && (
                        <a href={lead.live_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-[11px] text-emerald-500 hover:text-emerald-400 hover:underline inline-flex items-center gap-1" data-testid={`live-link-${lead.id}`}>
                          <ExternalLink className="h-3 w-3" />Live
                        </a>
                      )}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <p className="text-sm">{lead.contact_name}</p>
                  <p className="text-xs text-muted-foreground">{lead.phone}</p>
                </TableCell>
                <TableCell>
                  <span className="text-sm">{lead.area}{lead.city ? `, ${lead.city}` : ''}</span>
                  {lead.google_maps_url && (
                    <a href={lead.google_maps_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-0.5 ml-1" data-testid={`maps-link-${lead.id}`}>
                      <MapPin className="h-3 w-3" />Map
                    </a>
                  )}
                </TableCell>
                <TableCell>{lead.google_rating && <span className="flex items-center gap-1 text-sm"><Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />{lead.google_rating}</span>}</TableCell>
                <TableCell><button onClick={(e) => { e.stopPropagation(); openStatusModal(lead); }}>{getStatusBadge(lead.status)}</button></TableCell>
                <TableCell>
                  {lead.latest_remark ? (
                    <div className="max-w-[180px]">
                      <p className="text-xs truncate" title={lead.latest_remark}>{lead.latest_remark}</p>
                      {lead.latest_remark_at && <p className="text-[10px] text-muted-foreground">{new Date(lead.latest_remark_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</p>}
                    </div>
                  ) : <span className="text-xs text-muted-foreground">—</span>}
                </TableCell>
                <TableCell><span className="text-sm">{lead.assigned_to_name || '—'}</span></TableCell>
                <TableCell>
                  {lead.created_at ? (
                    <div className="text-xs">
                      <div>{lead.created_by_name || 'System'}</div>
                      <div className="text-muted-foreground">
                        {new Date(lead.created_at).toLocaleString('en-IN', {
                          day: '2-digit', month: 'short', year: '2-digit',
                          hour: '2-digit', minute: '2-digit', hour12: true,
                        })}
                      </div>
                    </div>
                  ) : <span className="text-xs text-muted-foreground">—</span>}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`smb-lead-actions-${lead.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/smb/leads/${lead.id}`)} data-testid="action-view"><Eye className="h-4 w-4 mr-2" />View Details</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openStatusModal(lead)} data-testid="action-status"><Edit className="h-4 w-4 mr-2" />Update Status</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleEdit(lead)} data-testid="action-edit"><Edit className="h-4 w-4 mr-2" />Edit Lead</DropdownMenuItem>
                      {lead.phone && <DropdownMenuItem onClick={() => window.open(`tel:${lead.phone}`)} data-testid="action-call"><Phone className="h-4 w-4 mr-2" />Call</DropdownMenuItem>}
                      {lead.whatsapp && <DropdownMenuItem onClick={() => window.open(`https://wa.me/${lead.whatsapp.replace(/[^0-9]/g, '')}`)} data-testid="action-whatsapp"><MessageCircle className="h-4 w-4 mr-2" />WhatsApp</DropdownMenuItem>}
                      {lead.google_maps_url && <DropdownMenuItem onClick={() => window.open(lead.google_maps_url)} data-testid="action-maps"><MapPin className="h-4 w-4 mr-2" />Maps</DropdownMenuItem>}
                      <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(lead)}>Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {leads.length === 0 && (
              <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">No leads found</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        {total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t text-sm" data-testid="leads-pagination">
            <div className="text-muted-foreground">
              Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Per page</span>
                <Select value={String(pageSize)} onValueChange={v => setPageSize(Number(v))}>
                  <SelectTrigger className="w-[80px] h-8" data-testid="leads-page-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="200">200</SelectItem>
                    <SelectItem value="500">500</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline" size="sm"
                disabled={page <= 1}
                onClick={() => setPage(1)}
                title="First page"
                data-testid="leads-first-page"
              >
                <ChevronsLeft className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline" size="sm"
                disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                data-testid="leads-prev-page"
              >
                Prev
              </Button>
              <span className="text-xs text-muted-foreground tabular-nums">
                Page {page} of {Math.max(1, Math.ceil(total / pageSize))}
              </span>
              <Button
                variant="outline" size="sm"
                disabled={page >= Math.ceil(total / pageSize)}
                onClick={() => setPage(p => p + 1)}
                data-testid="leads-next-page"
              >
                Next
              </Button>
              <Button
                variant="outline" size="sm"
                disabled={page >= Math.ceil(total / pageSize)}
                onClick={() => setPage(Math.max(1, Math.ceil(total / pageSize)))}
                title="Last page"
                data-testid="leads-last-page"
              >
                <ChevronsRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Dialog open={isOpen} onOpenChange={o => { if (!o) { setIsOpen(false); setEditId(null); } else setIsOpen(true); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? 'Edit Lead' : 'Add New Lead'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Business Name *</Label><Input value={form.business_name} onChange={e => setForm({ ...form, business_name: e.target.value })} required data-testid="form-business-name" /></div>
              <div className="space-y-1.5"><Label>Category</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}><SelectTrigger data-testid="form-category"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  {/* If editing a lead whose category is no longer active, still
                      show that value so it doesn't silently get dropped. */}
                  {form.category && !formCategories.some(c => c.slug === form.category) && (
                    <SelectItem key={form.category} value={form.category} className="capitalize">
                      {form.category} (inactive)
                    </SelectItem>
                  )}
                  {formCategories.map(c => (
                    <SelectItem key={c.slug} value={c.slug} className="capitalize">{c.label}</SelectItem>
                  ))}
                </SelectContent></Select>
              </div>
              <div className="space-y-1.5"><Label>Contact Name *</Label><Input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} required data-testid="form-contact-name" /></div>
              <div className="space-y-1.5"><Label>Phone *</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} required data-testid="form-phone" /></div>
              <div className="space-y-1.5"><Label>WhatsApp</Label><Input value={form.whatsapp} onChange={e => setForm({ ...form, whatsapp: e.target.value })} data-testid="form-whatsapp" /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="form-email" /></div>
              <div className="space-y-1.5"><Label>Area</Label><Input value={form.area} onChange={e => setForm({ ...form, area: e.target.value })} data-testid="form-area" /></div>
              <div className="space-y-1.5"><Label>City</Label><Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} data-testid="form-city" /></div>
              <div className="space-y-1.5"><Label>Google Maps URL</Label><Input value={form.google_maps_url} onChange={e => setForm({ ...form, google_maps_url: e.target.value })} data-testid="form-maps-url" /></div>
              <div className="space-y-1.5"><Label>Google Rating</Label><Input type="number" step="0.1" min="0" max="5" value={form.google_rating} onChange={e => setForm({ ...form, google_rating: e.target.value })} data-testid="form-rating" /></div>
              <div className="space-y-1.5"><Label>Preview URL</Label><Input value={form.preview_url} onChange={e => setForm({ ...form, preview_url: e.target.value })} data-testid="form-preview-url" /></div>
              <div className="space-y-1.5"><Label>Assign To</Label>
                <UserPicker value={form.assigned_to} onChange={v => setForm({ ...form, assigned_to: v })} placeholder="Search employees..." data-testid="form-assigned-to" />
              </div>
            </div>
            <div className="space-y-1.5"><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="form-notes" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsOpen(false)} disabled={isSubmitting}>Cancel</Button>
              <Button type="submit" data-testid="form-submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving…' : (editId ? 'Update' : 'Add Lead')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Status Change Modal */}
      <Dialog open={statusModal} onOpenChange={o => { if (!o) setStatusModal(false); }}>
        <DialogContent className="sm:max-w-md" data-testid="status-change-modal">
          <DialogHeader>
            <DialogTitle>Update Status</DialogTitle>
            <DialogDescription>{statusLead?.business_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={statusValue} onValueChange={setStatusValue}>
                <SelectTrigger data-testid="status-modal-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {statuses.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {PREVIEW_URL_STATUSES.includes(statusValue) && (
              <div className="space-y-2">
                <Label>Preview URL <span className="text-red-400">*</span></Label>
                <Input value={statusPreviewUrl} onChange={e => setStatusPreviewUrl(e.target.value)} placeholder="https://preview.myzow.com/..." data-testid="status-modal-preview-url" />
              </div>
            )}
            {LIVE_URL_STATUSES.includes(statusValue) && (
              <div className="space-y-2">
                <Label>Live URL <span className="text-red-400">*</span></Label>
                <Input value={statusLiveUrl} onChange={e => setStatusLiveUrl(e.target.value)} placeholder="https://example.com" data-testid="status-modal-live-url" />
              </div>
            )}
            <div className="space-y-2">
              <Label>Remark / Note</Label>
              <Textarea value={statusRemark} onChange={e => setStatusRemark(e.target.value)} placeholder="Add a remark for this status change..." rows={3} data-testid="status-modal-remark" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusModal(false)}>Cancel</Button>
            <Button onClick={handleStatusChange} data-testid="status-modal-submit">Update Status</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import CSV Dialog */}
      <Dialog open={importOpen} onOpenChange={o => { if (!o) setImportOpen(false); else setImportOpen(true); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle><FileSpreadsheet className="h-5 w-5 inline mr-2" />Bulk Import Leads from CSV</DialogTitle></DialogHeader>

          {!importResult ? (
            <div className="space-y-4">
              {/* Step 1: Download template */}
              <Card className="border-dashed">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">Step 1: Download Template</p>
                      <p className="text-xs text-muted-foreground">Get the CSV template with the correct column headers</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={handleDownloadTemplate} data-testid="download-template-btn">
                      <Download className="h-4 w-4 mr-2" />Download Template
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Step 2: Upload CSV */}
              <Card className="border-dashed">
                <CardContent className="py-4">
                  <p className="font-medium text-sm mb-2">Step 2: Upload your CSV</p>
                  <p className="text-xs text-muted-foreground mb-3">Required columns: business_name, contact_name, phone. Duplicates detected by phone number.</p>
                  <div className="flex items-center gap-3">
                    <label className="flex-1 cursor-pointer">
                      <input type="file" accept=".csv" onChange={handleFileSelect} className="hidden" data-testid="csv-file-input" />
                      <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors" data-testid="csv-dropzone">
                        {importFile ? (
                          <div className="flex items-center justify-center gap-2">
                            <FileSpreadsheet className="h-5 w-5 text-primary" />
                            <span className="text-sm font-medium">{importFile.name}</span>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => { e.preventDefault(); resetImport(); }} data-testid="clear-file"><X className="h-3 w-3" /></Button>
                          </div>
                        ) : (
                          <div>
                            <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">Click to select CSV file</p>
                          </div>
                        )}
                      </div>
                    </label>
                  </div>
                </CardContent>
              </Card>

              {/* Step 3: Preview */}
              {importPreview && (
                <Card>
                  <CardContent className="py-4">
                    <p className="font-medium text-sm mb-2">Step 3: Preview ({importPreview.totalRows} rows found)</p>
                    <div className="overflow-x-auto max-h-[250px] overflow-y-auto border rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs w-10">#</TableHead>
                            {importPreview.headers.slice(0, 6).map(h => <TableHead key={h} className="text-xs">{h}</TableHead>)}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {importPreview.rows.slice(0, 10).map((row, i) => (
                            <TableRow key={i}>
                              <TableCell className="text-xs text-muted-foreground">{row._row}</TableCell>
                              {importPreview.headers.slice(0, 6).map(h => (
                                <TableCell key={h} className="text-xs max-w-[150px] truncate">{row[h] || <span className="text-muted-foreground/50">-</span>}</TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    {importPreview.totalRows > 10 && <p className="text-xs text-muted-foreground mt-2">Showing first 10 of {importPreview.totalRows} rows</p>}
                  </CardContent>
                </Card>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button>
                <Button onClick={handleImport} disabled={!importFile || importing} data-testid="import-submit-btn">
                  {importing ? 'Importing...' : `Import ${importPreview?.totalRows || 0} Leads`}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            /* Import Results */
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <Card><CardContent className="py-4 text-center">
                  <p className="text-2xl font-bold">{importResult.total_rows}</p>
                  <p className="text-xs text-muted-foreground">Total Rows</p>
                </CardContent></Card>
                <Card className="border-green-500/30"><CardContent className="py-4 text-center">
                  <CheckCircle2 className="h-5 w-5 mx-auto mb-1 text-green-500" />
                  <p className="text-2xl font-bold text-green-400">{importResult.imported}</p>
                  <p className="text-xs text-muted-foreground">Imported</p>
                </CardContent></Card>
                <Card className={importResult.skipped > 0 ? "border-red-500/30" : ""}><CardContent className="py-4 text-center">
                  {importResult.skipped > 0 && <AlertTriangle className="h-5 w-5 mx-auto mb-1 text-red-500" />}
                  <p className={`text-2xl font-bold ${importResult.skipped > 0 ? 'text-red-400' : ''}`}>{importResult.skipped}</p>
                  <p className="text-xs text-muted-foreground">Skipped</p>
                </CardContent></Card>
              </div>

              {importResult.skipped_details?.length > 0 && (
                <Card className="border-red-500/20">
                  <CardContent className="py-4">
                    <p className="font-medium text-sm mb-2 text-red-400">Skipped Rows</p>
                    <div className="overflow-x-auto max-h-[200px] overflow-y-auto">
                      <Table>
                        <TableHeader><TableRow>
                          <TableHead className="text-xs">Row</TableHead>
                          <TableHead className="text-xs">Business</TableHead>
                          <TableHead className="text-xs">Phone</TableHead>
                          <TableHead className="text-xs">Reason</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                          {importResult.skipped_details.map((s, i) => (
                            <TableRow key={i}>
                              <TableCell className="text-xs">{s.row}</TableCell>
                              <TableCell className="text-xs">{s.business_name}</TableCell>
                              <TableCell className="text-xs">{s.phone}</TableCell>
                              <TableCell className="text-xs text-red-400">{s.errors.join(', ')}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => { resetImport(); }}>Import More</Button>
                <Button onClick={() => { setImportOpen(false); resetImport(); }} data-testid="import-done-btn">Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
